import { AnyAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import initialAIAssistantState, { initialChatThreadState } from './ai-assistant-initial-state';
import { ChatMessageDisplay } from '../models/chat-message.model';
import { AiModelInfo } from '../models/ai-model-info.model';

// Same slice name as aiAssistantSlice, so its reducer understands the main slice's message actions
// when they are wrapped in applyToThread.
const chatThreadSlice = createSlice({
  name: 'aiAssistant',
  initialState: initialChatThreadState,
  reducers: {
    addMessage: (state, action: PayloadAction<ChatMessageDisplay>) => {
      state.messages.push(action.payload);
    },
    appendToLastMessage: (state, action: PayloadAction<string>) => {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === 'assistant') {
        last.content += action.payload;
        last.statusText = undefined;
        if (last.steps) {
          const thinkingStep = last.steps.find(s => s.toolName === '__thinking__' && s.status === 'running');
          if (thinkingStep) thinkingStep.status = 'done';
          // The first real answer text means any still-open reasoning segment is over,
          // even if a reasoning_done event was missed for some reason.
          const reasoningStep = last.steps.find(s => s.kind === 'reasoning' && s.status === 'running');
          if (reasoningStep) reasoningStep.status = 'done';
        }
      }
    },
    setLastMessageStatus: (state, action: PayloadAction<string>) => {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === 'assistant' && last.isStreaming) {
        last.statusText = action.payload;
      }
    },
    setLastMessageStreamingDone: (state) => {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === 'assistant') {
        last.isStreaming = false;
        last.statusText = undefined;
        // Always overwrite with the actual completion time - the timestamp set at
        // message creation reflects request start, not when the response finished.
        last.timestamp = new Date().toISOString();
        if (last.steps) {
          last.steps.forEach(step => { step.status = 'done'; });
        }
      }
    },
    setLastMessageError: (state, action: PayloadAction<string>) => {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === 'assistant') {
        last.isStreaming = false;
        last.statusText = undefined;
        last.errorText = action.payload;
        // Always overwrite with the actual failure time, whether it's a timeout,
        // a server error, or a connection drop.
        last.timestamp = new Date().toISOString();
        if (last.steps) {
          last.steps.forEach(step => { step.status = 'done'; });
        }
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearMessages: (state) => {
      state.messages = [];
      state.error = null;
    },
    setMessages: (state, action: PayloadAction<ChatMessageDisplay[]>) => {
      state.messages = action.payload;
      state.error = null;
    },
    setLastMessageStopped: (state) => {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === 'assistant') {
        last.isStreaming = false;
        last.statusText = undefined;
        last.wasStopped = true;
        // Always overwrite with the actual stop time.
        last.timestamp = new Date().toISOString();
        if (last.steps) {
          last.steps.forEach(step => { step.status = 'done'; });
        }
      }
    },
    addStep: (state, action: PayloadAction<{ toolName: string; label: string }>) => {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === 'assistant' && last.isStreaming) {
        if (!last.steps) last.steps = [];
        if (action.payload.toolName !== '__thinking__') {
          const thinkingStep = last.steps.find(s => s.toolName === '__thinking__' && s.status === 'running');
          if (thinkingStep) thinkingStep.status = 'done';
        }
        last.steps.push({ toolName: action.payload.toolName, label: action.payload.label, status: 'running' });
      }
    },
    completeStep: (state, action: PayloadAction<{ toolName: string; label: string }>) => {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === 'assistant' && last.steps) {
        for (let i = last.steps.length - 1; i >= 0; i--) {
          if (last.steps[i].toolName === action.payload.toolName && last.steps[i].status === 'running') {
            last.steps[i].status = 'done';
            last.steps[i].label = action.payload.label;
            break;
          }
        }
      }
    },
    appendReasoningToLastMessage: (state, action: PayloadAction<string>) => {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === 'assistant' && last.isStreaming) {
        if (!last.steps) last.steps = [];
        const lastStep = last.steps[last.steps.length - 1];
        if (lastStep && lastStep.kind === 'reasoning' && lastStep.status === 'running') {
          lastStep.text = (lastStep.text ?? '') + action.payload;
        } else {
          const thinkingStep = last.steps.find(s => s.toolName === '__thinking__' && s.status === 'running');
          if (thinkingStep) thinkingStep.status = 'done';
          last.steps.push({ toolName: '__reasoning__', label: '', kind: 'reasoning', text: action.payload, status: 'running' });
        }
      }
    },
    completeReasoningOnLastMessage: (state) => {
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === 'assistant' && last.steps) {
        for (let i = last.steps.length - 1; i >= 0; i--) {
          if (last.steps[i].kind === 'reasoning' && last.steps[i].status === 'running') {
            last.steps[i].status = 'done';
            break;
          }
        }
      }
    }
  }
});

export const aiAssistantSlice = createSlice({
  name: 'aiAssistant',
  initialState: initialAIAssistantState,
  reducers: {
    ...chatThreadSlice.caseReducers,
    applyToThread: (state, action: PayloadAction<{ thread: string; action: AnyAction }>) => {
      const { thread, action: threadAction } = action.payload;
      if (!state.threads) state.threads = {};
      state.threads[thread] = chatThreadSlice.reducer(state.threads[thread] ?? initialChatThreadState, threadAction);
    },
    setPendingReportId: (state, action: PayloadAction<string | null>) => {
      state.pendingReportId = action.payload;
    },
    setModelInfo: (state, action: PayloadAction<AiModelInfo | null>) => {
      state.modelInfo = action.payload;
    }
  }
});

export const {
  addMessage,
  appendToLastMessage,
  setLastMessageStatus,
  setLastMessageStreamingDone,
  setLastMessageError,
  setLastMessageStopped,
  addStep,
  completeStep,
  appendReasoningToLastMessage,
  completeReasoningOnLastMessage,
  setLoading,
  setError,
  clearMessages,
  setMessages,
  setPendingReportId,
  setModelInfo,
  applyToThread
} = aiAssistantSlice.actions;

export default aiAssistantSlice.reducer;
